import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import StoreIcon from "@mui/icons-material/Store";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { allCompanyBranchesView, allUsers } from "../../../data/Axios/queries";
import {
  companyManagementUrl,
  userManagementUrl,
  AccountsandFinanceurl,
} from "../../../config";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material/styles";

const BusinessSummaryReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ✅ Read token once at the top
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const company = decodedToken.Company;
  const tokenBranchId = parseInt(decodedToken?.BranchId);

  // ✅ User is branch-locked when branchId is a valid positive number
  const isBranchLocked = !isNaN(tokenBranchId) && tokenBranchId > 0;


  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [branchesData, setBranchesData] = useState([]);
  const [usersData, setUsersData] = useState([]);

  // ✅ Default selectedBranch to locked branch ID when applicable
  const [selectedBranch, setSelectedBranch] = useState(
    isBranchLocked ? tokenBranchId : ""
  );
  const [selectedUser, setSelectedUser] = useState("");

  useEffect(() => {
    async function fetchAllData() {
      try {
        setIsLoading(true);

          const branchesResponse = await graphqlQuery(
            allCompanyBranchesView,
            companyManagementUrl.uri
          );
          const branches = Array.isArray(branchesResponse)
            ? branchesResponse
            : branchesResponse?.data?.allBranchView ||
              branchesResponse?.allBranchView ||
              [];
          setBranchesData(branches);
        

        const usersResponse = await graphqlQuery(
          allUsers,
          userManagementUrl.uri
        );
        const users = Array.isArray(usersResponse)
          ? usersResponse
          : usersResponse?.data?.allUsers ||
            usersResponse?.allUsers ||
            [];
        setUsersData(users);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
 const lockedBranchName =
  isBranchLocked
      ? `${branchesData.find((b) => b.id === parseInt(tokenBranchId)) ?.branchName }`
      : "Unknown";

  const generateBusinessSummaryHTML = async () => {
    try {
      const query = `
        query FinancialSummaryJsonDirect($input: sp_GetFinancialSummaryWithSalesAsStringInput!) {
          financialSummaryJsonDirect(sp_getFinancialSummaryWithSales: $input)
        }
      `;

      // ✅ Always use locked branch ID when applicable, ignoring selector value
      const resolvedBranchId = isBranchLocked
        ? tokenBranchId
        : selectedBranch
        ? parseInt(selectedBranch)
        : null;

      const variables = {
        input: {
          companyId: companyId,
          branchId: resolvedBranchId,
          regionId: null,
          createdBy: selectedUser ? parseInt(selectedUser) : null,
          startDate: startDate,
          endDate: endDate,
        },
      };

      const response = await graphqlQuery(
        query,
        AccountsandFinanceurl.uri,
        variables
      );
      let financialData =
        response?.data?.financialSummaryJsonDirect ||
        response?.financialSummaryJsonDirect ||
        response;

      let parsed;
      if (typeof financialData === "string") {
        try {
          parsed = JSON.parse(financialData);
          if (typeof parsed === "string") {
            parsed = JSON.parse(parsed);
          }
        } catch (e) {
          console.error("JSON Parse Error:", e);
          parsed = {};
        }
      } else {
        parsed = financialData;
      }

      const accountsSummary = parsed.AccountsSummary || [];
      const cashSales = parsed.CashSales || [];
      const customerReceivables = parsed.CustomerReceivables || [];
      const costOfGoods = parsed.CostOfGoods || [];
      const paymentVouchers = parsed.PaymentVouchers || [];
      const expenses = parsed.Expenses || [];

      const findAccount = (name) =>
        accountsSummary.find((a) => a.CategoryGroup === name) || {
          TotalDebit: 0,
          TotalCredit: 0,
          NetBalance: 0,
        };

      const salesRevenue = findAccount("Sales Revenue");
      const cogs = findAccount("Cost Of Goods Sold");
      const expensesAcc = findAccount("Expenses");
      const cashAcc = findAccount("Cash");
      const bankAcc = findAccount("Bank");
      const mpesaAcc = findAccount("Mpesa");
      const accountsPayable = findAccount("Accounts Payable");
      const accountsReceivable = findAccount("Accounts Receivable");

      const salesCash =
        cashSales.find((s) => s.PaymentMethod === "Cash")?.AmountPaid || 0;
      const salesMpesa =
        cashSales.find((s) => s.PaymentMethod === "Mpesa")?.AmountPaid || 0;
      const salesBank =
        cashSales.find((s) => s.PaymentMethod === "Bank")?.AmountPaid || 0;
      const salesCredit = cashSales.find((s) => s.PaymentMethod === "Credit")?.AmountPaid || 0;;

      const cogsCash =
        costOfGoods.find((c) => c.PaymentMethod === "Cash")?.AmountPaid || 0;
      const cogsMpesa =
        costOfGoods.find((c) => c.PaymentMethod === "Mpesa")?.AmountPaid || 0;
      const cogsBank =
        costOfGoods.find((c) => c.PaymentMethod === "Bank")?.AmountPaid || 0;
        const cogsSpendCredit =
        costOfGoods.find((c) => c.PaymentMethod === "Credit")?.AmountPaid || 0;

      const paidCash =
        paymentVouchers.find((p) => p.PaymentMethod === "Cash")?.AmountPaid ||
        0;
      const paidMpesa =
        paymentVouchers.find((p) => p.PaymentMethod === "Mpesa")?.AmountPaid ||
        0;
      const paidBank =
        paymentVouchers.find((p) => p.PaymentMethod === "Bank")?.AmountPaid ||
        0;

      const expensesCash =
        expenses.find((e) => e.PaymentMethod === "Cash")?.AmountPaid || 0;
      const expensesMpesa =
        expenses.find((e) => e.PaymentMethod === "MPESA")?.AmountPaid || 0;
      const expensesBank =
        expenses.find((e) => e.PaymentMethod === "Bank")?.AmountPaid || 0;

      const receivedCash =
        customerReceivables.find((p) => p.PaymentMethod === "Cash")
          ?.AmountPaid || 0;
      const receivedMpesa =
        customerReceivables.find((e) => e.PaymentMethod === "Mpesa")
          ?.AmountPaid || 0;
      const receivedBank =
        customerReceivables.find((p) => p.PaymentMethod === "Bank")
          ?.AmountPaid || 0;

      const netSalesRevenue =
        (salesRevenue.TotalCredit || 0) - (salesRevenue.TotalDebit || 0);
      const netCOGS = (cogs.TotalDebit || 0) - (cogs.TotalCredit || 0);
      const netExpenses =
        (expensesAcc.TotalDebit || 0) - (expensesAcc.TotalCredit || 0);
      const grossProfit = netSalesRevenue - netCOGS;
      const netProfit = grossProfit - netExpenses;

      // ✅ Branch name resolution — locked users always see their branch name
      const branchName = isBranchLocked
        ? lockedBranchName
        : branchesData.find((b) => b.id === parseInt(selectedBranch))
            ?.branchName || "All Branches";

      const selectedUserObj = usersData.find(
        (u) => u.id === parseInt(selectedUser)
      );
      const userName = selectedUserObj
        ? `${selectedUserObj.firstName} ${selectedUserObj.lastName}`
        : "All Users";

      const reportData = {
        companyName: company,
        branchName,
        userName,
        reportPeriod: `From ${dayjs(startDate).format(
          "MMMM D, YYYY"
        )} to ${dayjs(endDate).format("MMMM D, YYYY")}`,
        printDate: dayjs().format("DD/MM/YYYY hh:mm A"),

        salesRevenueDebit: salesRevenue.TotalDebit || 0,
        salesRevenueCredit: salesRevenue.TotalCredit || 0,
        accountsReceivableDebit: accountsReceivable.TotalDebit || 0,
        accountsReceivableCredit: accountsReceivable.TotalCredit || 0,
        cogsDebit: cogs.TotalDebit || 0,
        cogsCredit: cogs.TotalCredit || 0,
        expensesDebit: expensesAcc.TotalDebit || 0,
        expensesCredit: expensesAcc.TotalCredit || 0,
        accountsPayableDebit: accountsPayable.TotalDebit || 0,
        accountsPayableCredit: accountsPayable.TotalCredit || 0,
        cashDebit: cashAcc.TotalDebit || 0,
        cashCredit: cashAcc.TotalCredit || 0,
        bankDebit: bankAcc.TotalDebit || 0,
        bankCredit: bankAcc.TotalCredit || 0,
        mpesaDebit: mpesaAcc.TotalDebit || 0,
        mpesaCredit: mpesaAcc.TotalCredit || 0,
        salesCash,
        salesMpesa,
        salesBank,
        salesCredit,
        cogsCash,
        cogsMpesa,
        cogsBank,
        cogsSpendCredit,
        paidCash,
        paidMpesa,
        paidBank,
        expensesCash,
        expensesMpesa,
        expensesBank,
        receivedCash,
        receivedMpesa,
        receivedBank,

        netSalesRevenue,
        netCOGS,
        grossProfit,
        netExpenses,
        netProfit,
      };

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Business Summary - ${company}</title>
            <style>
                body {
                    font-family: 'Helvetica', 'Arial', sans-serif;
                    margin: 0;
                    padding: 0;
                    background: #f4f6f9;
                    font-size: 13px;
                    color: #333;
                }
                * { box-sizing: border-box; }
                .container {
                    width: 210mm;
                    min-height: 297mm;
                    margin: 20px auto;
                    background: white;
                    padding: 20mm 15mm;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                }
                .header {
                    text-align: center;
                    background: #1e5faf;
                    color: white;
                    padding: 15px;
                    margin-bottom: 20px;
                    border-radius: 6px;
                }
                .header h1 { margin: 0; font-size: 22px; font-weight: bold; }
                .info { margin-bottom: 20px; line-height: 1.6; }
                .info div { margin: 4px 0; }
                .buttons { text-align: center; margin-bottom: 20px; }
                .buttons button {
                    padding: 10px 20px;
                    margin: 0 10px;
                    background: #1e5faf;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .buttons button:hover { background: #155a9b; }
                .section { margin: 25px 0; }
                .section-title {
                    font-size: 16px;
                    font-weight: bold;
                    color: #1e5faf;
                    border-bottom: 2px solid #1e5faf;
                    padding-bottom: 6px;
                    margin-bottom: 10px;
                }
                table { width: 100%; border-collapse: collapse; margin-top: 8px; }
                th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #eee; }
                th { background: #f0f8ff; font-weight: bold; font-size: 13px; }
                .particulars { width: 60%; }
                .dr, .cr { text-align: right; width: 20%; }
                .indent { padding-left: 30px; }
                .subtitle td { font-weight: bold; background: #e3f2fd; }
                .total td {
                    font-weight: bold;
                    background: #f0f8ff;
                    border-top: 2px solid #1e5faf;
                }
                @media print {
                    body { background: white; padding: 0; }
                    .container { box-shadow: none; margin: 0; width: 100%; padding: 15mm; }
                    .buttons { display: none; }
                    @page { size: A4; margin: 10mm; }
                }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h1>BUSINESS SUMMARY REPORT</h1>
                </div>

                <div class="info">
                    <div><strong>Company:</strong> ${reportData.companyName}</div>
                    <div><strong>Branch:</strong> ${reportData.branchName}</div>
                    <div><strong>User:</strong> ${reportData.userName}</div>
                    <div><strong>Period:</strong> ${reportData.reportPeriod}</div>
                    <div style="text-align:right; margin-top:10px;">
                        <strong>Printed on:</strong> ${reportData.printDate}
                    </div>
                </div>

                <div class="buttons">
                    <button onclick="window.print()">🖨️ Print Report</button>
                    <button onclick="window.print(); setTimeout(() => window.close(), 1000);">
                        💾 Download PDF
                    </button>
                </div>

                <div class="section">
                    <div class="section-title">Sales Revenue Account</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="subtitle">
                                <td>Sales Revenue</td>
                                <td class="dr">${reportData.salesRevenueDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.salesRevenueCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td class="indent">Sales in Cash</td><td class="dr"></td><td class="cr">${reportData.salesCash.toLocaleString()}</td></tr>
                            <tr><td class="indent">Sales in Mpesa</td><td class="dr"></td><td class="cr">${reportData.salesMpesa.toLocaleString()}</td></tr>
                            <tr><td class="indent">Sales in Bank</td><td class="dr"></td><td class="cr">${reportData.salesBank.toLocaleString()}</td></tr>
                            <tr><td class="indent">Sales in Credit</td><td class="dr"></td><td class="cr">${reportData.salesCredit.toLocaleString()}</td></tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Account Receivable (Debtors)</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="subtitle">
                                <td>Account Receivable (Debtors)</td>
                                <td class="dr">${reportData.accountsReceivableDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.accountsReceivableCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td class="indent">Received in Cash</td><td class="dr"></td><td class="cr">${reportData.receivedCash.toLocaleString()}</td></tr>
                            <tr><td class="indent">Received in Mpesa</td><td class="dr"></td><td class="cr">${reportData.receivedMpesa.toLocaleString()}</td></tr>
                            <tr><td class="indent">Received in Bank</td><td class="dr"></td><td class="cr">${reportData.receivedBank.toLocaleString()}</td></tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Cost of Goods Sold Account</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="subtitle">
                                <td>Cost of Goods Sold</td>
                                <td class="dr">${reportData.cogsDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.cogsCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td class="indent">CoG Sold in Cash</td><td class="dr">${reportData.cogsCash.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">CoG Sold in Mpesa</td><td class="dr">${reportData.cogsMpesa.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">CoG Sold in Bank</td><td class="dr">${reportData.cogsBank.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">CoG Sold in Credit</td><td class="dr">${reportData.cogsSpendCredit.toLocaleString()}</td><td class="cr"></td></tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Account Payable (Creditors/Suppliers)</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="subtitle">
                                <td>Account Payable</td>
                                <td class="dr">${reportData.accountsPayableDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.accountsPayableCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td class="indent">Paid in Cash</td><td class="dr">${reportData.paidCash.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">Paid in Mpesa</td><td class="dr">${reportData.paidMpesa.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">Paid in Bank</td><td class="dr">${reportData.paidBank.toLocaleString()}</td><td class="cr"></td></tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Expenses Account</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="subtitle">
                                <td>Expenses</td>
                                <td class="dr">${reportData.expensesDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.expensesCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td class="indent">Paid in Cash</td><td class="dr">${reportData.expensesCash.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">Paid in Mpesa</td><td class="dr">${reportData.expensesMpesa.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td class="indent">Paid in Bank</td><td class="dr">${reportData.expensesBank.toLocaleString()}</td><td class="cr"></td></tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Accounts Summary</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td>Cash Account</td><td class="dr">${reportData.cashDebit.toLocaleString()}</td><td class="cr">${reportData.cashCredit.toLocaleString()}</td></tr>
                            <tr><td>Mpesa Account</td><td class="dr">${reportData.mpesaDebit.toLocaleString()}</td><td class="cr">${reportData.mpesaCredit.toLocaleString()}</td></tr>
                            <tr><td>Bank Account</td><td class="dr">${reportData.bankDebit.toLocaleString()}</td><td class="cr">${reportData.bankCredit.toLocaleString()}</td></tr>
                            <tr class="total">
                                <td><strong>Totals</strong></td>
                                <td class="dr"><strong>${(reportData.cashDebit + reportData.bankDebit + reportData.mpesaDebit).toLocaleString()}</strong></td>
                                <td class="cr"><strong>${(reportData.cashCredit + reportData.bankCredit + reportData.mpesaCredit).toLocaleString()}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="section">
                    <div class="section-title">Income Statement</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="particulars">Particulars</th>
                                <th class="dr">DR</th>
                                <th class="cr">CR</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="total">
                                <td>Sales Revenue</td>
                                <td class="dr">${reportData.salesRevenueDebit.toLocaleString()}</td>
                                <td class="cr">${reportData.salesRevenueCredit.toLocaleString()}</td>
                            </tr>
                            <tr><td>Less: Cost of Goods Sold</td><td class="dr">${reportData.netCOGS.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr class="total"><td>Gross Profit</td><td class="dr">${reportData.grossProfit.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr><td>Less: Expenses</td><td class="dr">${reportData.netExpenses.toLocaleString()}</td><td class="cr"></td></tr>
                            <tr class="total">
                                <td><strong>Net Profit</strong></td>
                                <td class="dr"><strong>${reportData.netProfit.toLocaleString()}</strong></td>
                                <td class="cr"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </body>
        </html>
      `;
    } catch (error) {
      console.error("Error generating report:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to generate report.",
      });
      return null;
    }
  };

  const handleGenerateBusinessSummary = async () => {
    setIsLoading(true);
    const html = await generateBusinessSummaryHTML();
    if (html) {
      const win = window.open("", "_blank");
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
    }
    setIsLoading(false);
  };

  return (
    <Card sx={{ p: 3, background: colors.primary[400] }}>
      {/* Title + locked branch badge */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Business Summary Report
        </Typography>
      </Box>

      <TextField
        label="Start Date"
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="End Date"
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputLabelProps={{ shrink: true }}
      />

      {/* ✅ Branch selector — locked indicator OR normal dropdown */}
      {isBranchLocked ? (
        // Read-only locked branch display
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
            height: 56,
          }}
        >
          <LockIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <StoreIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="body1" color="primary.main" fontWeight="600">
            {lockedBranchName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            (Branch locked)
          </Typography>
        </Box>
      ) : (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Branch (Optional)</InputLabel>
          <Select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            label="Branch (Optional)"
          >
            <MenuItem value="">
              <em>All Branches</em>
            </MenuItem>
            {branchesData.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>User (Optional)</InputLabel>
        <Select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          label="User (Optional)"
        >
          <MenuItem value="">
            <em>All Users</em>
          </MenuItem>
          {usersData.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.firstName} {u.lastName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        fullWidth
        onClick={handleGenerateBusinessSummary}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : "Generate Business Summary"}
      </Button>
    </Card>
  );
};

export default BusinessSummaryReport;